/* SpawnAnything — selection box in the setup (user request: hold + drag draws a white box, everything inside gets
   selected; never during a battle). Selectable: painted units and placed buildings. Selected things get white rings
   on the ground; Entf/Backspace deletes, R turns selected buildings, dragging a selected thing moves the whole
   selection, Esc or a click on empty ground clears it. main.js owns the data; this module gets it through deps. */
import * as THREE from 'three';
import './selectBox.css';

/**
 * Shift + drag is left to the camera (turn), Ctrl/⌘ + drag adds to the selection (and selects while painting).
 * deps: { canvas, camera, scene, groundH(x, z), groundAt(cx, cy) -> {x, z} | null, active() -> bool (setup + no menu),
 *         items() -> { units: [{ t, k, x, z, r }], buildings: [{ i, x, z, r }] },
 *         remove(sel), move(sel, dx, dz, done), rotate(sel), toast }
 * sel = { units: [{ t, k }], buildings: [i] }
 */
export function createSelectBox(deps) {
  const box = document.createElement('div'); box.className = 'aw-selbox'; box.hidden = true; document.body.appendChild(box);
  const hint = document.createElement('div'); hint.className = 'aw-selhint'; hint.hidden = true; document.body.appendChild(hint);
  let sel = { units: [], buildings: [] }, drag = null, moving = null;

  // white rings under every selected thing
  const ringGeo = new THREE.RingGeometry(0.78, 1, 28); ringGeo.rotateX(-Math.PI / 2);
  const ringMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95, depthWrite: false });
  let rings = null;
  function ensureRings(n) {
    if (rings && rings.instanceMatrix.count >= n) return;
    if (rings) { deps.scene.remove(rings); rings.dispose(); }
    rings = new THREE.InstancedMesh(ringGeo, ringMat, Math.max(256, n * 2)); rings.frustumCulled = false; rings.renderOrder = 3; rings.count = 0;
    rings.userData.noOutline = true; deps.scene.add(rings);
  }
  const _m = new THREE.Matrix4();
  function drawRings() {
    const it = deps.items(), n = sel.units.length + sel.buildings.length;
    ensureRings(n); let k = 0;
    const put = (x, z, r) => { _m.makeScale(r, 1, r); _m.setPosition(x, deps.groundH(x, z) + 0.06, z); rings.setMatrixAt(k++, _m); };
    const byKey = new Map(it.units.map(u => [u.t * 1e7 + u.k, u]));
    for (const s of sel.units) { const u = byKey.get(s.t * 1e7 + s.k); if (u) put(u.x, u.z, u.r * 1.5 + 0.25); }
    const bs = new Map(it.buildings.map(b => [b.i, b]));
    for (const i of sel.buildings) { const b = bs.get(i); if (b) put(b.x, b.z, b.r); }
    rings.count = k; rings.instanceMatrix.needsUpdate = true;
  }
  const count = () => sel.units.length + sel.buildings.length;
  function setSel(s) {
    sel = s; drawRings();
    const n = count();
    hint.hidden = !n;
    if (n) hint.innerHTML = `<b>${n.toLocaleString('de-DE')} ausgewählt</b> · <kbd>Entf</kbd> löschen · Ziehen verschiebt${sel.buildings.length ? ' · <kbd>R</kbd> dreht' : ''} · <kbd>Esc</kbd> aufheben`;
  }
  const clear = () => { if (count()) setSel({ units: [], buildings: [] }); };

  const _v = new THREE.Vector3();
  function inRect(x, z, r) {
    _v.set(x, deps.groundH(x, z) + 0.5, z).project(deps.camera);
    if (_v.z > 1) return false;
    const R = deps.canvas.getBoundingClientRect(), sx = (_v.x + 1) / 2 * R.width + R.left, sy = (1 - _v.y) / 2 * R.height + R.top;
    return sx >= drag.x0 && sx <= drag.x1 && sy >= drag.y0 && sy <= drag.y1;
  }
  function pickInBox(add) {
    const it = deps.items(), out = add ? { units: sel.units.slice(), buildings: sel.buildings.slice() } : { units: [], buildings: [] };
    const have = new Set(out.units.map(u => u.t * 1e7 + u.k)), haveB = new Set(out.buildings);
    for (const u of it.units) if (!have.has(u.t * 1e7 + u.k) && inRect(u.x, u.z, u.r)) out.units.push({ t: u.t, k: u.k });
    for (const b of it.buildings) if (!haveB.has(b.i) && inRect(b.x, b.z, b.r)) out.buildings.push(b.i);
    setSel(out);
  }
  // is the ground point on a selected thing? (start of a move)
  function onSelected(p) {
    if (!count() || !p) return false;
    const it = deps.items(), us = new Set(sel.units.map(u => u.t * 1e7 + u.k)), bs = new Set(sel.buildings);
    for (const u of it.units) if (us.has(u.t * 1e7 + u.k)) { const d = Math.hypot(u.x - p.x, u.z - p.z); if (d < u.r * 1.6 + 0.6) return true; }
    for (const b of it.buildings) if (bs.has(b.i) && Math.hypot(b.x - p.x, b.z - p.z) < b.r) return true;
    return false;
  }

  return {
    /** pointerdown on the canvas; returns true if the selection took the press */
    down(e, { paintMode }) {
      if (!deps.active() || e.pointerType === 'touch' || e.button !== 0) return false;
      if (e.shiftKey) return false; // Shift + drag turns the camera (like in the battle)
      const p = deps.groundAt(e.clientX, e.clientY);
      if (onSelected(p) && !e.shiftKey) { moving = { x: p.x, z: p.z, dx: 0, dz: 0 }; return true; }
      if (paintMode && !(e.ctrlKey || e.metaKey)) return false; // painting keeps the plain drag
      drag = { sx: e.clientX, sy: e.clientY, x0: e.clientX, y0: e.clientY, x1: e.clientX, y1: e.clientY, add: e.ctrlKey || e.metaKey, on: false }; // Ctrl/⌘: add to the selection
      return true;
    },
    move(e) {
      if (moving) {
        const p = deps.groundAt(e.clientX, e.clientY); if (!p) return true;
        const dx = p.x - moving.x, dz = p.z - moving.z;
        if (Math.abs(dx) + Math.abs(dz) > 0.05) { deps.move(sel, dx, dz, false); moving.x = p.x; moving.z = p.z; drawRings(); }
        return true;
      }
      if (!drag) return false;
      drag.x0 = Math.min(drag.sx, e.clientX); drag.x1 = Math.max(drag.sx, e.clientX); drag.y0 = Math.min(drag.sy, e.clientY); drag.y1 = Math.max(drag.sy, e.clientY);
      if (!drag.on && drag.x1 - drag.x0 + drag.y1 - drag.y0 > 6) drag.on = true;
      if (drag.on) { box.hidden = false; Object.assign(box.style, { left: drag.x0 + 'px', top: drag.y0 + 'px', width: drag.x1 - drag.x0 + 'px', height: drag.y1 - drag.y0 + 'px' }); }
      return true;
    },
    /** pointerup; returns true if the selection handled it (false = treat as a plain click) */
    up() {
      if (moving) { deps.move(sel, 0, 0, true); moving = null; drawRings(); return true; }
      if (!drag) return false;
      const d = drag; drag = null; box.hidden = true;
      if (d.on) { drag = d; pickInBox(d.add); drag = null; return true; }
      clear(); return false; // a plain click: let the game handle it (possess, etc.)
    },
    key(e) {
      if (!count() || !deps.active()) return false;
      if (e.code === 'Delete' || e.code === 'Backspace') { deps.remove(sel); setSel({ units: [], buildings: [] }); return true; }
      if (e.code === 'Escape') { clear(); return true; }
      if (e.code === 'KeyR' && sel.buildings.length) { deps.rotate(sel); drawRings(); return true; }
      return false;
    },
    clear, refresh: drawRings, get selection() { return sel; }, get busy() { return !!(drag || moving); },
  };
}
